import { useNavigate } from 'react-router-dom';
import { MessageCircle, X, Mail, Phone, AlertCircle, Bug, HelpCircle, Zap, TrendingUp } from 'lucide-react';

// Support contact details
const SUPPORT_EMAIL = 'support@neurolaw.ai';
const SUPPORT_PHONE = '+91 9001239773';
const SUPPORT_PHONE_DISPLAY = '+91 9001239773';

// Support Modal Component
const SupportModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  if (!isOpen) return null;

  const supportReasons = [
    { icon: Bug, label: 'Report a Bug', description: 'Found something not working?' },
    { icon: HelpCircle, label: 'Technical Issues', description: 'Need help with features?' },
    { icon: Zap, label: 'Usage Limits', description: 'Credits or limits reached?' },
    { icon: AlertCircle, label: 'General Issues', description: 'Any other problems?' },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1001] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gray-900 flex items-center justify-center">
              <MessageCircle size={16} className="text-white" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Support & Help</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-6">
          We're here to help! Contact our support team for assistance with any issues, bugs, usage limits, or general problems you're experiencing.
        </p>

        {/* Support Reasons */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {supportReasons.map((reason, idx) => (
            <div key={idx} className="flex items-start gap-2 p-3 rounded-lg bg-gray-50 border border-gray-200">
              <reason.icon size={18} className="text-gray-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-gray-900">{reason.label}</p>
                <p className="text-xs text-gray-500">{reason.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Contact Info */}
        <div className="space-y-3 bg-gray-50 rounded-lg p-4 mb-4">
          <h3 className="text-sm font-medium text-gray-900">Contact Information</h3>
          
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="flex items-center gap-3 text-sm text-gray-700 hover:text-gray-900 transition-colors group"
          >
            <div className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center group-hover:border-gray-900 transition-colors">
              <Mail size={16} className="text-gray-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500">Email us</p>
              <p className="font-medium">{SUPPORT_EMAIL}</p>
            </div>
          </a>

          <a
            href={`tel:${SUPPORT_PHONE}`}
            className="flex items-center gap-3 text-sm text-gray-700 hover:text-gray-900 transition-colors group"
          >
            <div className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center group-hover:border-gray-900 transition-colors">
              <Phone size={16} className="text-gray-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500">Call us</p>
              <p className="font-medium">{SUPPORT_PHONE_DISPLAY}</p>
            </div>
          </a>
        </div>

        {/* Footer */}
        <div className="flex gap-2">
          <button
            onClick={() => {
              navigate('/pricing');
              onClose();
            }}
            className="flex-1 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          >
            <TrendingUp size={16} />
            Upgrade Plans
          </button>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="flex-1 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-center font-medium"
          >
            Email Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default SupportModal;
